<template>
  <div class="config-table">
    <div v-if="label" class="subtitle-2 button-group-label">
      {{ label }}
      <tooltip v-if="!hideTooltip" :text="tooltip" />
      <helpIcon v-if="helpContent" :content="helpContent" />
    </div>
    <dataTable
      :headers="tableHeaders"
      :items="tableItems"
      item-key="FieldId"
      no-data-text="No items added yet"
      :hideFooter=true
      disable-sort
      :items-per-page="tableItems.length"
    >
      <template v-for="header in tableHeadersWithToolTip" v-slot:[`header.${header.value}`]>
        <div class="header-with-tooltip" v-bind:key="header.value">
          <div>{{ header.text }}</div><tooltip class="tooltip" :text="header.tooltip"/>
        </div>
      </template>
      <template v-slot:item="{ item }">
          <tr>
            <td v-for="col in tableHeaders || []" v-bind:key="col.value">
              <template v-if="field = item.AvailableFieldList.find(field => field.FieldId === col.value)">
                <configField 
                  :id="field.FieldId"
                  :type="field.Type"
                  :label="field.Label"
                  :field="field"
                  :parentField = "item.FieldId"
                  :value="getConfigValue(item, field)"
                  :options="field.Options"
                  :category="parentField"
                  :width="field.Width"
                  :prefix="field.Prefix"
                  :placeholder="field.Placeholder"
                  @setConfigValue="(value) => setConfigValue({ item, value })"
                />
              </template>
              <template v-else></template>
            </td>
          </tr>
      </template>
    </dataTable>
  </div>
</template>
  
  <script>
/**
 * @fileoverview Config input field for dropdown tables
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import dataTable from "@/qux-common/qux22/components/organisms/data-table";
import helpIcon from "@/components/help-icon";
import tooltip from "@/components/tooltip";

export default {
  name: "configTable",
  components: {
    dataTable,
    helpIcon,
    tooltip,
    configField: () => import("./config-field"),
  },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    headers: {
      type: Array,
      default() {
        return [];
      }
    },
    helpContent: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    options: {
      type: Array,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    ruleId: {
      type: String,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: Object,
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
  },
  methods: {
    setConfigValue({ item, value }) {
      const configValue = this.value || {};
      const currentFieldValue = configValue[item.FieldId];
      configValue[item.FieldId] = { ...currentFieldValue, ...value.value};
      this.$emit("setConfigValue", configValue);
    },
    getConfigValue(parent, field) {
      const value = (
        this.mergedConfig[this.ruleId] &&
        this.mergedConfig[this.ruleId][this.category] &&
        this.mergedConfig[this.ruleId][this.category][this.id] &&
        this.mergedConfig[this.ruleId][this.category][this.id][parent.FieldId] &&
        this.mergedConfig[this.ruleId][this.category][this.id][parent.FieldId][field.FieldId]
      );

      return value;
    },
  },
  computed: {
    ...mapGetters({
      mergedConfig: "getMergedConfig"
    }),
    tableHeaders() {
      return this.headers || [];
    },
    tableHeadersWithToolTip() {
      let headers = this.headers || [];
      return headers.filter(header => header.tooltip);
    },
    tableItems() {
      return this.options;
    },
  },
};
</script>
  
<style lang="scss" scoped>
.header-with-tooltip {
  display: flex;
  gap: $qvo-spacing-8;
}
.config-table {
  display: flex;
  flex-direction: column;
  gap: $qvo-spacing-24;
}

.v-data-table {
  &::v-deep {
    .v-data-table__wrapper thead tr th[role="columnheader"][scope="col"] {
      white-space: pre;
    }
    td {
      vertical-align: baseline;
    }
  }
}
</style>